// Tiny external store bridging the (non-React) game loop and the React HUD.
import {useSyncExternalStore} from "react";
import type {BallOn, Ruleset} from "../../shared/rules";

export interface HudPlayer {
    name: string;
    score: number;
    highestBreak: number;
    connected: boolean;
    guest: boolean;
}

export interface Toast {
    id: number;
    text: string;
    kind: "info" | "foul" | "good" | "warn";
}

export interface HudState {
    mode: "practice" | "online";
    phase: "aiming" | "shooting" | "waiting" | "spectating" | "watching" | "over";
    gameId: string | null;
    you: number;
    turn: number;
    players: HudPlayer[];
    breakPoints: number;
    ballOn: BallOn;
    ballInHand: boolean;
    redsLeft: number;
    /** object balls left (anyball shows this instead of the reds) */
    ballsLeft: number;
    /** the ruleset of the frame on the table */
    ruleset: Ruleset;
    /** anyball: level scores with the table cleared, the re-spotted black decides */
    suddenDeath: boolean;
    status: "playing" | "over";
    winner: number | null;
    endReason: string | null;
    rematch: boolean[];
    /** online: both players signed in (shot clock, Elo) */
    ranked: boolean;
    /** ranked: the player at the table and when their shot clock runs out (Date.now() ms); null when not running */
    clock: {turn: number; deadline: number; totalMs: number} | null;
    /** ranked, frame over: each player's new Elo and the change */
    elo: {rating: number; delta: number}[] | null;
    hint: string | null;
    toasts: Toast[];
    searching: boolean;
    connection: "offline" | "connecting" | "online";
    pointerLocked: boolean;
}

let state: HudState = {
    mode: "practice",
    phase: "aiming",
    gameId: null,
    you: 0,
    turn: 0,
    players: [{name: "You", score: 0, highestBreak: 0, connected: true, guest: false}],
    breakPoints: 0,
    ballOn: "red",
    ballInHand: true,
    redsLeft: 15,
    ballsLeft: 15,
    ruleset: "anyball",
    suddenDeath: false,
    status: "playing",
    winner: null,
    endReason: null,
    rematch: [false, false],
    ranked: false,
    clock: null,
    elo: null,
    hint: null,
    toasts: [],
    searching: false,
    connection: "offline",
    pointerLocked: false,
};

const listeners = new Set<() => void>();
let toastId = 0;
export const MAX_TOASTS = 3;

export const GameStore = {
    get: () => state,
    set(patch: Partial<HudState>) {
        state = {...state, ...patch};
        listeners.forEach(l => l());
    },
    subscribe(listener: () => void) {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    },
    toast(text: string, kind: Toast["kind"] = "info", ms = 4500) {
        if (!text) return;
        const id = ++toastId;
        // collapse repeats (and keep at most 3 on screen) so quick shots don't pile up
        const others = state.toasts.filter(t => t.text !== text);
        GameStore.set({toasts: [...others.slice(-(MAX_TOASTS - 1)), {id, text, kind}]});
        setTimeout(() => GameStore.set({toasts: state.toasts.filter(t => t.id !== id)}), ms);
    },
};

export function useGameStore<T>(selector: (s: HudState) => T): T {
    return useSyncExternalStore(GameStore.subscribe, () => selector(state));
}
